import React from 'react';
import { useRouter } from 'next/router';

import clsx from 'clsx';
import { Button, LoaderScreen } from 'components/common';
import { ContainerBase } from '../containers/containerBase';
import { Title } from '../title';
import { useQuery } from '@tanstack/react-query';
import { casesService, GET_CASE_COUNT } from '@/api';
import { customEvent } from '@/lib';
import AppRoutes from '@/const/routes';
import { Carousel } from './Carousel';
import { useInternalCaseFilter } from '@/hooks';

export const Claims: React.FC = () => {
	const [showCarousel, setShowCarousel] = React.useState(false);
	const router = useRouter();
	const { data: cases, isLoading } = useQuery({
		queryKey: [GET_CASE_COUNT],
		queryFn: () => casesService.getByQuantity(20),
	});

	const { filterInternalCases } = useInternalCaseFilter();
	const casesData = filterInternalCases(cases?.data?.items || []);

	React.useEffect(() => {
		const mount = () => setShowCarousel(true);
		if ('requestIdleCallback' in window) {
			const id = (window as any).requestIdleCallback(mount, { timeout: 2000 });
			return () => (window as any).cancelIdleCallback?.(id);
		}
		const t = setTimeout(mount, 1500);
		return () => clearTimeout(t);
	}, []);

	return (
		<ContainerBase
			className={clsx(
				'pt-0 md:space-y-16 pb-[67px] md:pb-16 relative',
				'flex flex-col items-center w-full md:!px-0 min-h-[425px] lg:mt-[45px]'
			)}
		>
			<Title
				description="See if You Qualify in Less than 5 minutes"
				classNameTitle="transition-none leading-tight"
			>
				Find your <span className="text-primary">Claim</span> Today
			</Title>

			{isLoading ? (
				<LoaderScreen className="my-10" />
			) : showCarousel ? (
				<Carousel casesData={casesData} />
			) : (
				<CardsSkeleton />
			)}

			{!isLoading && (
				<Button
					decoration="primary"
					size="fit"
					className="!mt-10 lg:!mt-16 h-12 px-[23px]"
					onClick={() => {
						customEvent({
							action: 'explore_all_cases_click',
							category: 'landing',
							label: 'Explore All Cases',
							value: 'Explore All Cases',
							callback: () => router.push(AppRoutes.LANDING_CASES),
						});
					}}
				>
					See All
				</Button>
			)}
		</ContainerBase>
	);
};

function CardsSkeleton() {
	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
			{Array.from({ length: 6 }).map((_, i) => (
				<div key={i} className="rounded-xl border p-4 animate-pulse">
					<div className="h-6 w-2/3 bg-gray-200 rounded mb-3" />
					<div className="h-4 w-5/6 bg-gray-200 rounded mb-2" />
					<div className="h-32 bg-gray-200 rounded" />
				</div>
			))}
		</div>
	);
}
